import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ComponentSync, EditableItem, LL_H_C, LL_V_L, ModuleFE_Thunderstorm, TS_CollapsableContainerV2 } from '@nu-art/thunderstorm-frontend/index';
import { __stringify, exists } from '@nu-art/ts-common';
import { AdvisorDropdown_FindingView, AdvisorDropdown_Variable } from '../AdvisorUiComponents/AdvisorUiComponents.js';
import { valueID_No, valueType_Enumerated, valueType_Range, valueType_StandardRange, valueType_SubEnumerated, valueType_SubRange, valueType_Tuple, variableType_AttributeInstance, variableType_PrecipitatingFactor, variableType_RelievingFactor } from '@app/hcs-shared';
import { ModuleFE_Advisor_FindingView, ModuleFE_Advisor_Value, ModuleFE_Advisor_Variable, ModuleFE_EMInterface } from '@app/advisor-frontend';
import { Renderer_DataField_Value } from '../Renderer_DataFieldV2/Renderer_DataField_Value.js';
import { Renderer_DataField_Raw } from '../Renderer_DataFieldV2/Renderer_DataField_Raw.js';
import './Component_EditFindings.scss';
import { FadeScrollWrapper, ICONS } from '@app/styles';
import { advisorConfig_DevMode } from '../../../modules/ModuleFE_AdvisorSettings/types.js';
import { ModuleFE_AdvisorSettings } from '../../../modules/ModuleFE_AdvisorSettings/ModuleFE_AdvisorSettings.js';
import { ModuleFE_Spotlight } from '../../../modules/ModuleFE_Spotlight/index.js';
import { AdvisorConversion } from '../../../_utils/advisor-conversion.js';
import { ModuleFE_AdvisorMini } from '../../../modules/ModuleFE_AdvisorMini.js';
import { PanelKey_Home } from '../../../_shared.js';
const DDVariableTypes = ['normal', 'evidence-of', 'family-history-of', 'history-of', 'recent'];
export class Component_EditFindings extends ComponentSync {
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.variableTypeMap = this.generateVariableTypeMap();
        state.findings = this.getEditableFinding(nextProps.findings);
        return state;
    }
    //######################### Logic #########################
    generateVariableTypeMap() {
        return ModuleFE_Advisor_Variable.cache.all().reduce((map, variable) => {
            let editType;
            let grouping;
            //Get grouping
            switch (variable.type) {
                case variableType_AttributeInstance: {
                    const parentAttribute = ModuleFE_Advisor_Variable.cache.unique(variable.parents.attributeId);
                    switch (parentAttribute.type) {
                        case variableType_PrecipitatingFactor:
                            grouping = 'precipitating-factor';
                            break;
                        case variableType_RelievingFactor:
                            grouping = 'relieving-factor';
                            break;
                        default:
                            grouping = 'normal';
                    }
                    break;
                }
                default:
                    grouping = 'normal';
            }
            //Get edit type
            const value = ModuleFE_Advisor_Value.cache.unique(variable.valueId);
            switch (value?.type) {
                case valueType_Tuple:
                case valueType_Enumerated:
                case valueType_SubEnumerated:
                    editType = 'enumerated';
                    break;
                case valueType_Range:
                case valueType_StandardRange:
                case valueType_SubRange:
                    editType = 'numeric';
                    break;
                default:
                    return map;
            }
            map[variable._id] = { editType, grouping };
            return map;
        }, {});
    }
    getEditableFinding(findings) {
        const editable = new EditableItem(findings).setSaveAction(f => this.submitFindings(f));
        editable.setOnChanged(async () => this.forceUpdate());
        return editable;
    }
    addNewFinding = (variable) => {
        const type = this.state.variableTypeMap[variable._id].editType;
        const editable = this.state.findings.editProp('processedRawFindings', []);
        const rawFinding = { variableId: variable._id, type, isChiefComplaint: false };
        if (type === 'numeric') {
            const value = ModuleFE_Advisor_Value.cache.unique(variable.valueId);
            rawFinding.measurementUnitId = value.data.unitsId;
        }
        else {
            const attributes = ModuleFE_Advisor_Variable.cache.filter(attribute => {
                return attribute.type === 'attribute-instance' && attribute.parents.variableId === variable._id;
            });
            rawFinding.attributes = attributes.map(attribute => {
                return { variableId: attribute._id, type: 'enumerated' };
            });
        }
        editable.updateArrayAt(rawFinding);
    };
    filterVariable = (variable) => {
        if (!this.state.variableTypeMap[variable._id] || !DDVariableTypes.includes(variable.type))
            return false;
        return exists(this.state.selectedFV) ? ModuleFE_Advisor_FindingView.variableMatches.view(variable, this.state.selectedFV) : true;
    };
    submitFindings = async (findings) => {
        const normalized = AdvisorConversion.normalize_EMFindings(findings);
        try {
            await ModuleFE_EMInterface.updateEncounterApis.updateNeededVariables({ findings: normalized });
            this.closeEditFindings();
            return findings;
        }
        catch (err) {
            this.logError('Could not submit findings', err);
            return findings;
        }
    };
    closeEditFindings = () => {
        if (ModuleFE_AdvisorMini.getIsMiniActive()) {
            ModuleFE_AdvisorMini.openPanel(PanelKey_Home);
        }
        else {
            ModuleFE_Spotlight.closeSpotlight();
        }
    };
    //######################### Render #########################
    render() {
        const devMode = ModuleFE_AdvisorSettings.getAdvisorConfig(advisorConfig_DevMode);
        return _jsxs(LL_V_L, { className: 'edit-findings', tabIndex: devMode ? 0 : undefined, onPaste: devMode ? this.handlePaste : undefined, children: [this.render_SubHeader(devMode), this.render_Findings(), this.render_Footer()] });
    }
    // Dev-only: paste a previously copied findings snapshot onto the focused panel to skip manual re-entry.
    handlePaste = (e) => {
        // Only act when the panel root itself is focused; let pastes into inner inputs behave normally.
        if (e.target !== e.currentTarget)
            return;
        try {
            const parsed = JSON.parse(e.clipboardData.getData('text'));
            // Accept either a raw EMFindings snapshot (copy icon) or a backend payload ({patientData: {...}}).
            const findings = (parsed?.patientData ?? parsed);
            this.setState({ findings: this.getEditableFinding(findings) });
        }
        catch (err) {
            this.logError('Could not paste findings', err);
        }
    };
    render_SubHeader = (devMode) => {
        return _jsxs(LL_H_C, { className: 'edit-findings__sub-header', children: [_jsx(AdvisorDropdown_FindingView.selectable, { canUnselect: true, selected: this.state.selectedFV, onSelected: fv => this.setState({ selectedFV: fv }) }), _jsx(AdvisorDropdown_Variable.selectable, { limitItems: 50, queryFilter: this.filterVariable, onSelected: variable => this.addNewFinding(variable) }), devMode && _jsx(ICONS.copy.component, { className: 'edit-findings__copy', onClick: () => ModuleFE_Thunderstorm.copyToClipboard(__stringify(this.state.findings.item, true), 'Findings copied') })] });
    };
    render_Findings = () => {
        const toUpdate = this.state.findings.editProp('findingsToUpdate', []);
        const raw = this.state.findings.editProp('processedRawFindings', []);
        return _jsxs(FadeScrollWrapper, { className: 'edit-findings__list', children: [toUpdate.item.map((item, index) => {
                    const editable = toUpdate.editProp(index, {});
                    editable.setOnDelete(() => toUpdate.removeArrayItem(index));
                    return this.render_Finding(editable);
                }), raw.item.map((item, index) => {
                    const editable = raw.editProp(index, {});
                    editable.setOnDelete(() => raw.removeArrayItem(index));
                    return this.render_Finding(editable);
                })] });
    };
    //######################### Render - Needed Variable #########################
    render_Finding = (editable) => {
        if (editable.item.attributes?.length)
            return this.render_Finding_WithAttributes(editable);
        return this.render_Finding_Standard(editable);
    };
    render_Finding_Standard = (_editable) => {
        const varId = _editable.item.variableId;
        const type = _editable.item.type ?? this.state.variableTypeMap[varId];
        switch (type) {
            case 'enumerated': {
                const editable = _editable;
                return _jsx(Renderer_DataField_Value, { variableId: varId, selectedValueId: editable.item.valueIds?.[0], onValueSelected: value => editable.updateObj({ valueIds: value ? [value] : undefined }), allowUnknown: true, maxAllowedOptions: 4, containerSelector: '.provide-info__body' }, varId);
            }
            case 'numeric': {
                const editable = _editable;
                return _jsx(Renderer_DataField_Raw, { variableId: varId, value: editable.item.value, onValue: value => editable.updateObj({ value }), containerSelector: '.provide-info__body' }, varId);
            }
        }
    };
    render_Finding_WithAttributes = (editable) => {
        if (editable.item.type === 'enumerated' && (!editable.item.valueIds?.[0] || editable.item.valueIds[0] === valueID_No))
            return this.render_Finding_Standard(editable);
        const editableAttributes = editable.editProp('attributes', []);
        const hasPrecipitating = editableAttributes.item.some(att => this.state.variableTypeMap[att.variableId].grouping === 'precipitating-factor');
        const hasRelieving = editableAttributes.item.some(att => this.state.variableTypeMap[att.variableId].grouping === 'relieving-factor');
        return _jsx(TS_CollapsableContainerV2, { initialCollapsed: false, headerRenderer: () => this.render_Finding_Standard(editable), containerRenderer: () => {
                return _jsxs(_Fragment, { children: [editableAttributes.item?.map((att, index) => {
                            if (this.state.variableTypeMap[att.variableId]?.grouping !== 'normal')
                                return;
                            const editableAtt = editableAttributes.editProp(index, {});
                            return this.render_Finding_Standard(editableAtt);
                        }), hasPrecipitating && _jsx(TS_CollapsableContainerV2, { headerRenderer: () => 'Precipitating Factors', containerRenderer: () => editableAttributes.item?.map((att, index) => {
                                if (this.state.variableTypeMap[att.variableId]?.grouping !== 'precipitating-factor')
                                    return;
                                const editableAtt = editableAttributes.editProp(index, {});
                                return this.render_Finding_Standard(editableAtt);
                            }), animated: true, forceUpdate: true }), hasRelieving && _jsx(TS_CollapsableContainerV2, { headerRenderer: () => 'Relieving Factors', containerRenderer: () => editableAttributes.item?.map((att, index) => {
                                if (this.state.variableTypeMap[att.variableId]?.grouping !== 'relieving-factor')
                                    return;
                                const editableAtt = editableAttributes.editProp(index, {});
                                return this.render_Finding_Standard(editableAtt);
                            }), animated: true, forceUpdate: true })] });
            }, animated: true, forceUpdate: true }, editable.item.variableId);
    };
    //######################### Render - Footer #########################
    render_Footer = () => {
        return _jsxs(LL_H_C, { className: 'edit-findings__footer', children: [_jsx(Button, { variant: 'secondary', onClick: this.closeEditFindings, children: "Cancel" }), _jsx(Button, { variant: 'primary', onClick: () => this.state.findings.save(), children: "Submit" })] });
    };
}
//# sourceMappingURL=Component_EditFindings.js.map